import { Button } from "@/components/ui/button";
import { formatPocet, sklonuj } from "@/lib/format";
import type { HromadnaAkcia, VyberRiadkov } from "@/components/cennik/cennikZalozky";

const POLOZKA = ["položka", "položky", "položiek"] as const;

type Props = {
  vyber: VyberRiadkov;
  /** Celá zobrazená strana je zaškrtnutá — vtedy ponúkni celý filter. */
  celaStrana: boolean;
  /** Počet riadkov podľa filtra (všetky strany). */
  total: number;
  /** Záložka Hrany — výrobca sa tam volá kolekcia. */
  hrany: boolean;
  /** Pohľad Len vyradené — namiesto Vyradiť je Obnoviť. */
  lenVyradene: boolean;
  onVsetkyPodlaFiltra: () => void;
  onZrusit: () => void;
  onAkcia: (akcia: HromadnaAkcia) => void;
};

/**
 * Lišta hromadných úprav nad tabuľkou Cenníka (KOLO 166).
 *
 * Gmailový vzor: zaškrtnutá celá strana ponúkne „Vybrať všetkých N
 * podľa filtra" — server potom vyberie riadky tým istým filtrom a overí,
 * že ich je stále N. Bez výberu sa lišta neukazuje.
 */
export function HromadnaLista({
  vyber,
  celaStrana,
  total,
  hrany,
  lenVyradene,
  onVsetkyPodlaFiltra,
  onZrusit,
  onAkcia,
}: Props): JSX.Element | null {
  const podlaFiltra = vyber.rezim === "filter";
  const pocet = podlaFiltra ? total : vyber.ids.size;
  if (pocet === 0) return null;

  return (
    <div
      role="region"
      aria-label="Hromadné úpravy"
      className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 text-sm"
    >
      <span className="font-medium">
        {podlaFiltra
          ? `Vybraté všetky ${formatPocet(total)} ${sklonuj(total, POLOZKA)} podľa filtra`
          : `${sklonuj(pocet, ["Vybraná", "Vybrané", "Vybraných"])} ${formatPocet(pocet)} ${sklonuj(pocet, POLOZKA)}`}
      </span>
      {!podlaFiltra && celaStrana && total > pocet ? (
        <button
          type="button"
          onClick={onVsetkyPodlaFiltra}
          className="font-medium text-primary underline underline-offset-2"
        >
          Vybrať všetkých {formatPocet(total)} podľa filtra
        </button>
      ) : null}
      <button
        type="button"
        onClick={onZrusit}
        className="text-muted-foreground underline underline-offset-2"
      >
        Zrušiť výber
      </button>
      <div className="ml-auto flex flex-wrap gap-2">
        <Button size="sm" variant="outline" onClick={() => onAkcia("kategoria")}>
          Zmeniť kategóriu
        </Button>
        <Button size="sm" variant="outline" onClick={() => onAkcia("vyrobca")}>
          {hrany ? "Nastaviť kolekciu" : "Nastaviť výrobcu"}
        </Button>
        <Button size="sm" variant="outline" onClick={() => onAkcia("marze")}>
          Nastaviť marže
        </Button>
        <Button size="sm" variant="outline" onClick={() => onAkcia("marze_kategorie")}>
          Marže podľa kategórie
        </Button>
        {lenVyradene ? (
          <Button size="sm" variant="outline" onClick={() => onAkcia("obnovit")}>
            Obnoviť
          </Button>
        ) : (
          <Button size="sm" variant="outline" onClick={() => onAkcia("vyradit")}>
            Vyradiť
          </Button>
        )}
      </div>
    </div>
  );
}
